<template>
  <footer class="app-footer">
    <div class="footer-container">
      <!-- Footer Links -->
      <div class="footer-links-wrapper">

        <!-- Column 1: Brand Info & Policies -->
        <div class="footer-column">
          <h3 @click="toggleSection('about')" class="footer-toggle-heading">
            <span>{{ props.appName }}</span>
            <i class="fas" :class="openSections.about ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </h3>
          <ul :class="{ 'is-open': openSections.about }">
            <li><a :href="route('about')">About Us</a></li>
            <li><a :href="route('terms')">Terms & Conditions</a></li>
            <li><a :href="route('policies')">Privacy Policy</a></li>
          </ul>
        </div>

        <!-- Column 2: Customers / Support -->
        <div class="footer-column">
          <h3 @click="toggleSection('service')" class="footer-toggle-heading">
            <span>Customers</span>
            <i class="fas" :class="openSections.service ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </h3>
          <ul :class="{ 'is-open': openSections.service }">
            <li><a :href="route('contact')">Contact Us</a></li>
            <li><a :href="route('tracking')">Track Order</a></li>
            <li><a :href="route('refund')">Return Policy</a></li>
          </ul>
        </div>

        <!-- Column 3: Shopping -->
        <div class="footer-column">
          <h3 @click="toggleSection('shop')" class="footer-toggle-heading">
            <span>Shopping</span>
            <i class="fas" :class="openSections.shop ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </h3>
          <ul :class="{ 'is-open': openSections.shop }">
            <li><a :href="route('clearance')">Clearance</a></li>
            <li><a :href="route('trending')">Trending</a></li>
            <li><a :href="route('today-deals')">Today's Deals</a></li>
          </ul>
        </div>

        <!-- Column 4: Media & Payments -->
        <div class="footer-column">
          <h3 @click="toggleSection('follow')" class="footer-toggle-heading">
            <span>Media</span>
            <i class="fas" :class="openSections.follow ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </h3>
          <div class="footer-content" :class="{ 'is-open': openSections.follow }">
            <div class="footer-social">
              <a :href="`https://facebook.com/${socialHandle}`" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><i class="fab fa-facebook-f"></i></a>
              <a :href="`https://instagram.com/${socialHandle}`" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><i class="fab fa-instagram"></i></a>
              <a :href="`https://twitter.com/${socialHandle}`" target="_blank" rel="noopener noreferrer" aria-label="Twitter"><i class="fab fa-twitter"></i></a>
              <a :href="`https://youtube.com/@${socialHandle}`" target="_blank" rel="noopener noreferrer" aria-label="YouTube"><i class="fab fa-youtube"></i></a>
              <a :href="`https://tiktok.com/@${socialHandle}`" target="_blank" rel="noopener noreferrer" aria-label="TikTok"><i class="fab fa-tiktok"></i></a>
            </div>
            <h4 class="payment-heading">Payment Methods</h4>
            <div class="payment-methods">
              <i class="fab fa-cc-visa"></i>
              <i class="fab fa-cc-mastercard"></i>
              <i class="fab fa-cc-paypal"></i>
              <i class="fab fa-cc-amex"></i>
            </div>
          </div>
        </div>

      </div>

      <!-- Copyright Bar -->
      <div class="footer-copyright">
        <p>Copyright &copy; {{ currentYear }} {{ (props.appName || 'REGIIS COSMETICS').toUpperCase() }}. All Rights Reserved.
          <a :href="route('terms')">User Agreement</a>,
          <a :href="route('policies')">Privacy</a> and
          <a :href="route('policies')">Cookies</a>
        </p>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { computed, reactive } from 'vue'

const props = defineProps({
  appName: {
    type: String,
    default: 'Regiis Cosmetics'
  },
  appUrl: {
    type: String,
    default: '/'
  }
})

const currentYear = computed(() => new Date().getFullYear())

const socialHandle = computed(() => {
  return (props.appName || 'regiiscosmetics').toLowerCase().replace(/\s+/g, '')
})

const openSections = reactive({
  about: false,
  service: false,
  shop: false,
  follow: false
})

const toggleSection = (section) => {
  openSections[section] = !openSections[section]
}

const route = (name) => {
  const routes = {
    'home': '/',
    'clearance': '/clearance',
    'under-10k': '/under-10k',
    'cart.view': '/cart',
    'dashboard': '/dashboard',
    'about': '/about',
    'contact': '/contact',
    'refund': '/refund',
    'trending': '/trending',
    'today-deals': '/today-deals',
    'policies': '/policies',
    'terms': '/terms',
    'tracking': '/track',
    'sitemap': '/sitemap'
  }
  return routes[name] || '/'
}
</script>

<style scoped>
.app-footer {
  background: #ffffff;
  border-top: 1px solid #f0f0f0;
  width: 100%;
}

.footer-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 1.5rem 1.5rem;
}

.footer-links-wrapper {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin-bottom: 2.5rem;
}

.footer-column {
  display: flex;
  flex-direction: column;
}

.footer-toggle-heading {
  font-size: 1.1rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.footer-toggle-heading i {
  display: none;
}

.footer-column ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-column ul li {
  margin-bottom: 0.75rem;
}

.footer-column ul li a {
  color: #4b5563;
  text-decoration: none;
  font-size: 0.95rem;
  transition: color 0.2s ease;
}

.footer-column ul li a:hover {
  color: #111827;
}

.footer-content {
  display: block;
}

.footer-social {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.footer-social a {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #f3f4f6;
  color: #374151;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.footer-social a:hover {
  background: #111827;
  color: #ffffff;
}

.payment-heading {
  font-size: 0.95rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.payment-methods {
  display: flex;
  gap: 0.75rem;
  font-size: 1.75rem;
  color: #4b5563;
}

.footer-copyright {
  border-top: 1px solid #f0f0f0;
  padding: 1.75rem 0 0.5rem;
  text-align: center;
}

.footer-copyright p {
  color: #4b5563;
  font-size: 0.85rem;
  margin: 0;
  line-height: 1.6;
}

.footer-copyright a {
  color: #c2410c;
  text-decoration: underline;
}

/* ==========================================================================
   MOBILE ACCORDION FOLDING STYLES (MATCHING EXACT REGIISCOSMETICS REFERENCE IMAGE)
   ========================================================================== */
@media (max-width: 768px) {
  .footer-container {
    padding: 1rem 1.25rem 2rem !important;
  }

  .footer-links-wrapper {
    display: flex !important;
    flex-direction: column !important;
    gap: 0 !important;
    margin-bottom: 1.5rem !important;
  }

  .footer-column {
    border-bottom: 1px solid #f0f0f0 !important;
    padding: 0 !important;
    width: 100% !important;
  }

  .footer-column:last-child {
    border-bottom: 1px solid #f0f0f0 !important;
  }

  .footer-toggle-heading {
    cursor: pointer;
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    width: 100% !important;
    padding: 0.875rem 0 !important;
    margin: 0 !important;
    font-size: 1.05rem !important;
    font-weight: 700 !important;
    color: #000000 !important;
    user-select: none;
  }

  .footer-toggle-heading i {
    display: block !important;
    font-size: 0.85rem !important;
    color: #4b5563 !important;
    transition: transform 0.3s ease;
  }

  .footer-column ul {
    max-height: 0;
    overflow: hidden;
    opacity: 0;
    transition: max-height 0.35s ease, opacity 0.3s ease, padding 0.35s ease;
    padding: 0 !important;
    margin: 0 !important;
  }

  .footer-column ul.is-open {
    max-height: 500px;
    opacity: 1;
    padding: 0.25rem 0 0.875rem !important;
  }

  .footer-column ul li {
    margin-bottom: 0.5rem;
  }

  .footer-column ul li a {
    font-size: 0.9rem;
    color: #4b5563;
  }

  .footer-content {
    max-height: 0;
    overflow: hidden;
    opacity: 0;
    transition: max-height 0.35s ease, opacity 0.3s ease, padding 0.35s ease;
    padding: 0 !important;
  }

  .footer-content.is-open {
    max-height: 500px;
    opacity: 1;
    padding: 0.25rem 0 1rem !important;
  }

  .footer-copyright {
    border-top: none !important;
    padding: 1.25rem 0 0.5rem !important;
    text-align: center !important;
  }

  .footer-copyright p {
    font-size: 0.8rem !important;
    text-align: center !important;
    line-height: 1.6 !important;
  }
}
</style>
